import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Loader2, User, Factory, Send } from "lucide-react";
import { api } from "@/lib/api";

export default function TransferModal({ open, onClose, onSuccess, currentBalance, unlimited }) {
  const { t } = useTranslation();
  const downOnBackdropRef = useRef(false);
  const [destType, setDestType] = useState("user");
  const [destId, setDestId] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setDestType("user");
    setDestId("");
    setAmount("");
    setNote("");
    setError(null);
    setSubmitting(false);
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = useCallback(async () => {
    setError(null);
    const n = parseInt(amount, 10);
    if (!Number.isFinite(n) || n <= 0) {
      setError(t("billing.transfer.error_insufficient"));
      return;
    }
    if (!unlimited && n > (currentBalance || 0)) {
      setError(t("billing.transfer.error_insufficient"));
      return;
    }
    if (!destId.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post("/tokens/transfer", {
        destination_type: destType,
        destination_id: destId.trim(),
        amount: n,
        note: note.trim() || undefined,
      });
      onSuccess?.({ amount: n, ...res.data });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = typeof detail === "string" ? detail : detail?.error;
      const errMap = {
        destination_user_not_found: t("billing.transfer.error_not_found"),
        cannot_transfer_to_self: t("billing.transfer.error_self"),
        insufficient_balance: t("billing.transfer.error_insufficient"),
      };
      setError(errMap[code] || t("billing.checkout.failed"));
    } finally {
      setSubmitting(false);
    }
  }, [amount, destType, destId, note, currentBalance, unlimited, onSuccess, t]);

  if (!open) return null;

  return (
    <div
      data-testid="transfer-overlay"
      className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/65 backdrop-blur-sm animate-fade-in-up px-4 py-6 overflow-y-auto"
      role="dialog"
      onMouseDown={(e) => {
        downOnBackdropRef.current = e.target === e.currentTarget;
      }}
      onMouseUp={(e) => {
        const ok = downOnBackdropRef.current && e.target === e.currentTarget && !submitting;
        downOnBackdropRef.current = false;
        if (ok) onClose();
      }}
    >
      <div
        data-testid="transfer-modal"
        className="relative w-full max-w-[380px] rounded-[24px] px-5 sm:px-6 pt-6 pb-5 bg-[rgba(10,14,26,0.96)] backdrop-blur-xl border border-[rgba(0,229,255,0.3)] shadow-[0_30px_80px_-10px_rgba(0,0,0,0.75)]"
      >
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          aria-label={t("common.close")}
          data-testid="transfer-close"
          className="absolute top-3 right-3 h-8 w-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-white/[0.06] hover:text-[#E5E7EB] disabled:opacity-40"
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>
        <h2 className="text-[18px] font-semibold text-[#E5E7EB] pr-8">
          {t("billing.transfer.title")}
        </h2>

        {/* Destination type toggle */}
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setDestType("user")}
            data-testid="transfer-type-user"
            data-active={destType === "user" ? "true" : "false"}
            className={`h-10 rounded-xl text-[12px] inline-flex items-center justify-center gap-1.5 ${destType === "user" ? "bg-[rgba(0,229,255,0.12)] border border-[rgba(0,229,255,0.45)] text-[#00E5FF]" : "bg-white/[0.03] border border-white/[0.08] text-[#E5E7EB]"}`}
          >
            <User className="h-3.5 w-3.5" strokeWidth={1.75} />
            {t("billing.transfer.to_user")}
          </button>
          <button
            type="button"
            onClick={() => setDestType("aiko")}
            data-testid="transfer-type-aiko"
            data-active={destType === "aiko" ? "true" : "false"}
            className={`h-10 rounded-xl text-[12px] inline-flex items-center justify-center gap-1.5 ${destType === "aiko" ? "bg-[rgba(0,229,255,0.12)] border border-[rgba(0,229,255,0.45)] text-[#00E5FF]" : "bg-white/[0.03] border border-white/[0.08] text-[#E5E7EB]"}`}
          >
            <Factory className="h-3.5 w-3.5" strokeWidth={1.75} />
            {t("billing.transfer.to_aiko")}
          </button>
        </div>

        <div className="mt-3 space-y-2.5">
          <input
            type="text"
            value={destId}
            onChange={(e) => setDestId(e.target.value)}
            placeholder={destType === "user" ? t("billing.transfer.destination_user") : t("billing.transfer.destination_aiko")}
            data-testid="transfer-destination"
            className="w-full h-10 px-3 rounded-xl bg-white/[0.03] border border-white/[0.08] text-[12.5px] text-[#E5E7EB] placeholder:text-[#6B7280] focus:outline-none focus:border-[rgba(0,229,255,0.4)]"
          />
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={t("billing.transfer.amount")}
            min="1"
            data-testid="transfer-amount"
            className="w-full h-10 px-3 rounded-xl bg-white/[0.03] border border-white/[0.08] text-[12.5px] text-[#E5E7EB] placeholder:text-[#6B7280] focus:outline-none focus:border-[rgba(0,229,255,0.4)] font-mono"
          />
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t("billing.transfer.note")}
            rows={2}
            maxLength={200}
            data-testid="transfer-note"
            className="w-full px-3 py-2 rounded-xl bg-white/[0.03] border border-white/[0.08] text-[12.5px] text-[#E5E7EB] placeholder:text-[#6B7280] focus:outline-none focus:border-[rgba(0,229,255,0.4)] resize-none"
          />
        </div>

        {error ? (
          <p data-testid="transfer-error" className="mt-3 px-3 py-2 rounded-xl text-[11.5px] text-[#F87171] bg-[rgba(239,68,68,0.06)] border border-[rgba(239,68,68,0.3)]">
            {error}
          </p>
        ) : null}

        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting || !destId.trim() || !amount}
          data-testid="transfer-send"
          className="mt-4 w-full h-11 rounded-full text-[13px] font-semibold bg-[rgba(0,229,255,0.18)] border border-[rgba(0,229,255,0.55)] text-[#00E5FF] hover:bg-[rgba(0,229,255,0.28)] shadow-[0_0_22px_rgba(0,229,255,0.35)] disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
        >
          {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} /> : <Send className="h-3.5 w-3.5" strokeWidth={2} />}
          {t("billing.transfer.send")}
        </button>
      </div>
    </div>
  );
}
